<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { advanceVideoRenderClock, buildVideoStreamUrl, computeAdaptiveVideoPlaybackSpeed, computeVideoFrameDrop, selectAv1WebCodecsConfig, resolveAv1Backend, resolveVideoRenderSize, shouldRevealVideo } from '../domain/videoDecoder.js';
import { videoLogger } from '../services/runtimeLogger.js';
import { releaseAssetUrl, runtimeAssetUrl } from '../services/runtimeAssets.js';

const props = defineProps({
  service: { type: Object, required: true },
  tableIndex: { type: Number, required: true },
  dataMode: { type: String, default: 'demo' },
  resumeEvent: { type: Object, default: () => ({ id: 0 }) },
  t: { type: Function, required: true }
});
const emit = defineEmits(['state']);
const canvas = ref(null);
const canvasRevision = ref(0);
const waiting = ref(true);
const errorMessage = ref('');
const decoderBackend = ref('');
let worker = null;
let connectSequence = 0;
let workerSequence = 0;
let firstFrameTimer = 0;
let noMetadataTimer = 0;
let maskForceTimer = 0;
let activeStreamUrl = '';
let activeTableIndex = -1;
let activeBackend = '';
let webCodecsDisabled = false;
let webCodecsConfig = null;
let frameWidth = 1920;
let frameHeight = 1105;
let unexpectedFrameLogged = false;
let firstFrameDrawn = false;
let sawFastStartMeta = false;
let fastStartActive = true;
let fastStartEndSeen = false;
let noMetadataGraceExpired = false;
let connectStartedAt = 0;
let activeState = 'waiting';
let activeMessageKey = 'vueDesktop.status.connectingVideo';
let canvasContext = null;
let canvasTransferred = false;
let pageSuspended = false;
const firstFrameTimeoutMs = 5000;
const noMetadataGraceMs = 800;
const maskForceTimeoutMs = 10000;
const videoBaseFrameMs = 100;
const videoQueueHardMax = 12;
const videoQueueCatchupStart = 3;
const videoQueueCatchupFull = 20;
const videoPlaybackSpeedMax = 4;
const videoSpeedChangePerSecond = 0.3;
const videoLowLatencyTargetFrames = 10;
let videoFrameQueue = [];
let videoRenderFrame = 0;
let videoPlaybackSpeed = 1;
let videoLastRenderAt = 0;
let videoSpeedLastUpdateAt = 0;
const decoderOverride = new URLSearchParams(window.location.search).get('vdec')
  || new URLSearchParams(window.location.search).get('dec')
  || '';
const wasmWorkerUrl = runtimeAssetUrl(import.meta.env.DEV ? '/wasm/open.js?v=1.3.0' : '/video.min.js?v=1.3.0');

function statusFallback(key) {
  return {
    'vueDesktop.status.connectingVideo': 'Connecting to the VIP video',
    'vueDesktop.status.videoStartupFailed': 'Video player failed to start',
    'vueDesktop.status.videoProcessingFailed': 'Video processing failed. Please try again later.',
    'vueDesktop.status.videoConnectionFailed': 'Video connection failed. Please try again later.'
  }[key] || '';
}

function setState(state, message = '', messageKey = '') {
  activeState = state;
  activeMessageKey = messageKey;
  waiting.value = state !== 'playing';
  errorMessage.value = message;
  emit('state', { state, message, backend: activeBackend });
}

function clearFirstFrameTimer() {
  window.clearTimeout(firstFrameTimer);
  firstFrameTimer = 0;
}

function clearRevealTimers() {
  window.clearTimeout(noMetadataTimer);
  window.clearTimeout(maskForceTimer);
  noMetadataTimer = 0;
  maskForceTimer = 0;
}

function resetRevealState() {
  clearRevealTimers();
  firstFrameDrawn = false;
  sawFastStartMeta = false;
  fastStartActive = true;
  fastStartEndSeen = false;
  noMetadataGraceExpired = false;
  connectStartedAt = performance.now();
}

function revealIfReady(reason, forceExpired = false) {
  if (!shouldRevealVideo({
    hasFrame: firstFrameDrawn,
    sawFastStartMeta,
    fastStartActive,
    fastStartEndSeen,
    noMetadataGraceExpired,
    forceExpired
  })) return false;
  clearRevealTimers();
  if (canvas.value) canvas.value.dataset.videoCatchup = 'complete';
  setState('playing');
  videoLogger.info('[wanli-video] catch-up hidden until stable frame', {
    tableIndex: activeTableIndex,
    reason,
    fastStartEndSeen,
    durationMs: Math.round(performance.now() - connectStartedAt)
  });
  return true;
}

function stopWorker() {
  clearFirstFrameTimer();
  clearRevealTimers();
  workerSequence += 1;
  resetVideoFrameQueue();
  if (!worker) return;
  try { worker.postMessage(['Disconnect']); } catch (error) { videoLogger.error('[wanli-video] disconnect failed', error); }
  worker.terminate();
  worker = null;
}

function disconnect() {
  connectSequence += 1;
  activeStreamUrl = '';
  activeTableIndex = -1;
  activeBackend = '';
  decoderBackend.value = '';
  webCodecsDisabled = false;
  stopWorker();
  const node = canvas.value;
  if (node) {
    if (!canvasTransferred && canvasContext) canvasContext.clearRect(0, 0, node.width, node.height);
    delete node.dataset.videoTable;
    delete node.dataset.videoStreamPath;
    delete node.dataset.videoCatchup;
    delete node.dataset.videoBackend;
    delete node.dataset.videoPlayback;
    delete node.dataset.videoCatchupSeen;
    delete node.dataset.videoStats;
    delete node.dataset.videoAccelerationPreference;
    delete node.dataset.videoRenderer;
  }
  canvasContext = null;
}

function getCanvasContext(node) {
  if (!canvasContext && node) {
    canvasContext = node.getContext('2d', { alpha: false, desynchronized: true });
  }
  return canvasContext;
}

function drawFrame(payload) {
  const node = canvas.value;
  if (!node || !payload) return false;
  const raw = payload?.data && ArrayBuffer.isView(payload.data) ? payload.data : payload;
  const bytes = raw instanceof Uint8ClampedArray ? raw : new Uint8ClampedArray(raw);
  if (bytes.length !== frameWidth * frameHeight * 4) {
    const inferredHeight = bytes.length % (frameWidth * 4) === 0 ? bytes.length / (frameWidth * 4) : 0;
    if (inferredHeight > 0) frameHeight = inferredHeight;
  }
  if (bytes.length !== frameWidth * frameHeight * 4) {
    if (!unexpectedFrameLogged) {
      unexpectedFrameLogged = true;
      videoLogger.error('[wanli-video] unexpected decoded frame size', {
        bytes: bytes.length,
        width: frameWidth,
        height: frameHeight
      });
    }
    return false;
  }
  if (node.width !== frameWidth) node.width = frameWidth;
  if (node.height !== frameHeight) node.height = frameHeight;
  getCanvasContext(node)?.putImageData(new ImageData(bytes, frameWidth, frameHeight), 0, 0);
  return true;
}

function drawBitmap(bitmap, meta = {}) {
  const node = canvas.value;
  if (!node || !bitmap) return false;
  const width = Math.max(1, Number(meta.width) || Number(bitmap.width) || frameWidth);
  const height = Math.max(1, Number(meta.height) || Number(bitmap.height) || frameHeight);
  frameWidth = width;
  frameHeight = height;
  if (node.width !== width) node.width = width;
  if (node.height !== height) node.height = height;
  const context = getCanvasContext(node);
  if (!context) {
    bitmap.close?.();
    return false;
  }
  try {
    context.drawImage(bitmap, 0, 0, width, height);
    return true;
  } finally {
    bitmap.close?.();
  }
}

function computeVideoPlaybackSpeed(queueSize) {
  return computeAdaptiveVideoPlaybackSpeed(queueSize, {
    catchupStart: videoQueueCatchupStart,
    catchupFull: videoQueueCatchupFull,
    min: 1,
    max: 1.25
  });
}

function resetVideoFrameQueue() {
  if (videoRenderFrame) window.cancelAnimationFrame(videoRenderFrame);
  videoRenderFrame = 0;
  videoFrameQueue = [];
  videoPlaybackSpeed = 1;
  videoLastRenderAt = 0;
  videoSpeedLastUpdateAt = 0;
}

function enqueueVideoFrame(payload, frameMeta) {
  if (!payload) return;
  videoFrameQueue.push({ payload, frameMeta });
  if (videoFrameQueue.length > videoQueueHardMax) {
    failVideoPlayback('decoded-queue-overflow', { queuedFrames: videoFrameQueue.length });
    return;
  }
  if (!videoRenderFrame) videoRenderFrame = window.requestAnimationFrame(renderVideoFrameQueue);
}

function acknowledgeFrame(frame) {
  if (frame?.frameMeta?.frameId !== undefined && worker) {
    const buffer = frame.payload instanceof ArrayBuffer ? frame.payload : null;
    const acknowledgement = { sid: frame.frameMeta.sid, frameId: frame.frameMeta.frameId };
    // Return ownership only after putImageData is complete. The worker checks
    // sid/frameId/size before recycling, so old or duplicate ACKs are harmless.
    if (buffer?.byteLength) {
      acknowledgement.buffer = buffer;
      worker.postMessage(['FrameConsumed', acknowledgement], [buffer]);
    } else worker.postMessage(['FrameConsumed', acknowledgement]);
  }
}

function failVideoPlayback(reason, detail = {}) {
  videoLogger.error('[wanli-video] playback stopped', { reason, tableIndex: activeTableIndex, backend: activeBackend, ...detail });
  stopWorker();
  setState('error', props.t('vueDesktop.status.videoProcessingFailed', 'Video processing failed. Please try again later.'), 'vueDesktop.status.videoProcessingFailed');
}

function renderVideoFrameQueue(now) {
  videoRenderFrame = 0;
  let queueSize = videoFrameQueue.length;
  if (!queueSize) return;
  const queuedFastStart = fastStartActive || videoFrameQueue.some((frame) => Boolean(frame.frameMeta?.fastStartActive));
  const drop = computeVideoFrameDrop({
    queueSize,
    fastStart: waiting.value && queuedFastStart,
    targetFrames: videoLowLatencyTargetFrames
  });
  if (drop.count) {
    videoFrameQueue.splice(0, drop.count).forEach(acknowledgeFrame);
    queueSize = videoFrameQueue.length;
  }
  const backlogFrames = Math.max(queueSize, Number(videoFrameQueue[0]?.frameMeta?.backlogFrames) || 0);
  const targetSpeed = queuedFastStart
    ? videoPlaybackSpeedMax
    : computeVideoPlaybackSpeed(backlogFrames);
  if (queuedFastStart) {
    videoPlaybackSpeed = videoPlaybackSpeedMax;
  } else {
    videoPlaybackSpeed = Math.min(videoPlaybackSpeed, 1.25);
    if (!videoSpeedLastUpdateAt) videoSpeedLastUpdateAt = now;
    const elapsedForSpeed = Math.min(1000, Math.max(0, now - videoSpeedLastUpdateAt));
    const maxDelta = videoSpeedChangePerSecond * (elapsedForSpeed / 1000);
    const speedDelta = targetSpeed - videoPlaybackSpeed;
    videoPlaybackSpeed += Math.abs(speedDelta) > maxDelta ? Math.sign(speedDelta) * maxDelta : speedDelta;
  }
  videoSpeedLastUpdateAt = now;
  const interval = Math.max(16, videoBaseFrameMs / videoPlaybackSpeed);
  if (!videoLastRenderAt || now - videoLastRenderAt >= interval) {
    const frame = videoFrameQueue.shift();
    if (drawFrame(frame.payload)) {
      handleDecodedFrame(frame.frameMeta);
      videoLastRenderAt = advanceVideoRenderClock(videoLastRenderAt, now, interval);
    }
    acknowledgeFrame(frame);
    if (canvas.value) {
      canvas.value.dataset.videoQueuedFrames = String(videoFrameQueue.length);
      canvas.value.dataset.videoPlaybackSpeed = videoPlaybackSpeed.toFixed(3);
    }
  }
  if (videoFrameQueue.length) videoRenderFrame = window.requestAnimationFrame(renderVideoFrameQueue);
}

function handleDecodedFrame(frameMeta) {
  clearFirstFrameTimer();
  firstFrameDrawn = true;
  if (frameMeta && typeof frameMeta === 'object' && frameMeta.fastStartActive !== undefined) {
    fastStartActive = Boolean(frameMeta.fastStartActive);
    if (canvas.value) {
      canvas.value.dataset.videoPlayback = fastStartActive ? 'catchup' : 'steady';
      if (fastStartActive) canvas.value.dataset.videoCatchupSeen = 'true';
    }
  }
  if (!sawFastStartMeta && !noMetadataTimer) {
    noMetadataTimer = window.setTimeout(() => {
      noMetadataTimer = 0;
      noMetadataGraceExpired = true;
      revealIfReady('no-fast-start-metadata');
    }, noMetadataGraceMs);
  }
  revealIfReady('stable-frame-after-fast-start');
}

async function recoverDecoder(reason, sequence, tableIndex) {
  if (sequence !== connectSequence || tableIndex !== activeTableIndex || activeBackend !== 'webcodecs') return;
  const failedPreference = webCodecsConfig?.hardwareAcceleration || 'prefer-hardware';
  // Fence duplicate timeout/error callbacks before awaiting a capability probe.
  activeBackend = 'selecting';
  stopWorker();
  const nextConfig = failedPreference === 'prefer-hardware'
    ? await selectAv1WebCodecsConfig(window, { excludePreferences: ['prefer-hardware'] })
    : null;
  if (sequence !== connectSequence || tableIndex !== activeTableIndex) return;
  webCodecsConfig = nextConfig;
  webCodecsDisabled = !nextConfig;
  const nextBackend = nextConfig ? 'webcodecs' : 'wasm';
  videoLogger.warn('[wanli-video] decoder transition', {
    tableIndex, reason, failedPreference, nextBackend,
    nextPreference: nextConfig?.hardwareAcceleration || 'wasm-software'
  });
  if (canvasTransferred) {
    canvasTransferred = false;
    canvasContext = null;
    canvasRevision.value += 1;
    await nextTick();
  }
  startWorker(nextBackend, sequence, tableIndex);
}

function startWorker(backend, sequence, tableIndex) {
  if (sequence !== connectSequence || tableIndex !== activeTableIndex || !activeStreamUrl) return;
  stopWorker();
  resetRevealState();
  activeBackend = backend;
  decoderBackend.value = backend;
  if (canvas.value) {
    canvas.value.dataset.videoBackend = backend;
    canvas.value.dataset.videoRenderer = backend === 'webcodecs' ? 'worker-or-bitmap' : 'main';
    canvas.value.dataset.videoTable = String(tableIndex + 1);
    canvas.value.dataset.videoStreamPath = new URL(activeStreamUrl).pathname;
  }
  const currentWorkerSequence = ++workerSequence;
  const workerUrl = backend === 'webcodecs' ? runtimeAssetUrl('/webcodes/av1-worker.js?v=1.3.0') : wasmWorkerUrl;
  setState('waiting', props.t('vueDesktop.status.connectingVideo', 'Connecting to the VIP video'), 'vueDesktop.status.connectingVideo');
  if (canvas.value) canvas.value.dataset.videoCatchup = 'active';
  try {
    worker = new Worker(workerUrl);
  } catch (error) {
    videoLogger.error('[wanli-video] worker creation failed', { backend, error });
    if (backend === 'webcodecs') return recoverDecoder('worker-create-failed', sequence, tableIndex);
    setState('error', props.t('vueDesktop.status.videoStartupFailed', 'Video player failed to start'), 'vueDesktop.status.videoStartupFailed');
    return;
  }
  worker.addEventListener('error', (event) => {
    if (sequence !== connectSequence || tableIndex !== activeTableIndex || currentWorkerSequence !== workerSequence) return;
    videoLogger.error('[wanli-video] worker error', { backend, message: event.message || String(event) });
    if (backend === 'webcodecs') recoverDecoder('worker-error', sequence, tableIndex);
    else setState('error', props.t('vueDesktop.status.videoStartupFailed', 'Video player failed to start'), 'vueDesktop.status.videoStartupFailed');
  });
  worker.addEventListener('message', (event) => {
    if (sequence !== connectSequence || tableIndex !== activeTableIndex || currentWorkerSequence !== workerSequence || !Array.isArray(event.data)) {
      if (event.data?.[0] === 'VideoBitmap') event.data?.[1]?.close?.();
      return;
    }
    const [type, payload, frameMeta] = event.data;
    if (type === 'VideoSize') {
      const width = Number(event.data[1]);
      const height = Number(event.data[2]);
      if (width > 0 && height > 0) {
        frameWidth = width;
        frameHeight = height;
        videoLogger.info('[wanli-video] decoded frame size', { width, height, backend });
      }
    } else if (type === 'VideoBitmap' && drawBitmap(payload, frameMeta)) {
      handleDecodedFrame(frameMeta);
    } else if (type === 'VideoRendered' && payload && typeof payload === 'object') {
      if (canvas.value) canvas.value.dataset.videoRenderer = 'worker';
      handleDecodedFrame(payload);
    } else if (type === 'VideoData') {
      enqueueVideoFrame(payload, frameMeta);
    } else if (type === 'FastStartMeta' && payload && typeof payload === 'object') {
      sawFastStartMeta = true;
      if (payload.active !== undefined) fastStartActive = Boolean(payload.active);
      if (payload.end) {
        fastStartActive = false;
        fastStartEndSeen = true;
      }
      videoLogger.info('[wanli-video] fast-start metadata', { tableIndex, active: fastStartActive, end: Boolean(payload.end) });
    } else if (type === 'DecodeFallback') {
      if (backend === 'webcodecs') recoverDecoder(payload?.reason || 'decode-fallback', sequence, tableIndex);
      else setState('error', props.t('vueDesktop.status.videoProcessingFailed', 'Video processing failed. Please try again later.'), 'vueDesktop.status.videoProcessingFailed');
    } else if (type === 'VideoError') {
      failVideoPlayback(payload?.reason || 'worker-error', payload?.detail);
    } else if (type === 'VideoStats' && canvas.value) {
      canvas.value.dataset.videoStats = JSON.stringify(payload);
    } else if (type === 'DecoderReady' && payload) {
      if (canvas.value) {
        canvas.value.dataset.videoRenderer = payload.renderer;
        canvas.value.dataset.videoAccelerationPreference = payload.accelerationPreference || 'wasm-software';
      }
      videoLogger.info('[wanli-video] decoder ready', { tableIndex, ...payload });
    }
  });
  let initOptions = backend === 'wasm'
    ? { codecType: 1, codec: 'av1', width: frameWidth, height: frameHeight, frameAcknowledgements: true, recycleBuffers: true }
    : { hardwareAcceleration: webCodecsConfig?.hardwareAcceleration || 'prefer-hardware' };
  let transfer = [];
  if (canvas.value && typeof canvas.value.transferControlToOffscreen === 'function' && typeof OffscreenCanvas !== 'undefined') {
    try {
      const renderCanvas = canvas.value.transferControlToOffscreen();
      const renderSize = resolveVideoRenderSize({
        cssWidth: canvas.value.getBoundingClientRect().width,
        devicePixelRatio: window.devicePixelRatio,
        sourceWidth: frameWidth,
        sourceHeight: frameHeight
      });
      canvasTransferred = true;
      initOptions = { ...initOptions, renderCanvas, renderWidth: renderSize.width, renderHeight: renderSize.height };
      transfer = [renderCanvas];
    } catch (error) {
      canvasTransferred = false;
      videoLogger.warn('[wanli-video] direct worker canvas unavailable; using main canvas', { tableIndex, backend, error });
    }
  }
  worker.postMessage(['Init', initOptions], transfer);
  worker.postMessage(['ConnetToWebSocket', activeStreamUrl, `videovip${tableIndex + 1}`, sequence]);
  maskForceTimer = window.setTimeout(() => {
    maskForceTimer = 0;
    if (firstFrameDrawn) {
      videoLogger.warn('[wanli-video] forcing transition after catch-up timeout', { tableIndex, timeoutMs: maskForceTimeoutMs });
      revealIfReady('catch-up-timeout', true);
    }
  }, maskForceTimeoutMs);
  if (backend === 'webcodecs') {
    firstFrameTimer = window.setTimeout(() => recoverDecoder('av1-no-first-frame', sequence, tableIndex), firstFrameTimeoutMs);
  }
  videoLogger.info('[wanli-video] decoder started', { backend, tableIndex, streamPath: new URL(activeStreamUrl).pathname });
}

async function connect(requestedTableIndex = props.tableIndex) {
  if (document.hidden || pageSuspended) {
    pageSuspended = true;
    disconnect();
    setState('waiting', '', 'vueDesktop.status.connectingVideo');
    return;
  }
  const tableIndex = Math.max(0, Math.min(7, Number(requestedTableIndex) || 0));
  disconnect();
  if (canvasTransferred) {
    canvasTransferred = false;
    canvasContext = null;
    canvasRevision.value += 1;
    await nextTick();
  }
  setState('waiting');
  if (props.dataMode === 'demo') return;
  const sequence = ++connectSequence;
  activeTableIndex = tableIndex;
  try {
    const forcedWasm = resolveAv1Backend({ override: decoderOverride, webCodecsSupported: true }) === 'wasm';
    const selectedConfig = forcedWasm ? null : await selectAv1WebCodecsConfig(window);
    if (sequence !== connectSequence || tableIndex !== activeTableIndex || tableIndex !== props.tableIndex) return;
    webCodecsConfig = selectedConfig;
    const backend = resolveAv1Backend({
      override: decoderOverride,
      webCodecsSupported: Boolean(webCodecsConfig),
      webCodecsDisabled
    });
    const grant = await props.service.requestVideoGrant(tableIndex);
    if (sequence !== connectSequence || tableIndex !== activeTableIndex || tableIndex !== props.tableIndex) return;
    activeStreamUrl = buildVideoStreamUrl({ location: window.location, tableIndex, grant });
    if (canvas.value) {
      canvas.value.dataset.videoTable = String(tableIndex + 1);
      canvas.value.dataset.videoStreamPath = new URL(activeStreamUrl).pathname;
    }
    startWorker(backend, sequence, tableIndex);
  } catch (error) {
    if (sequence !== connectSequence) return;
    videoLogger.error('[wanli-video] connect failed', error);
    setState('error', props.t('vueDesktop.status.videoConnectionFailed', 'Video connection failed. Please try again later.'), 'vueDesktop.status.videoConnectionFailed');
  }
}

function suspendForPageBackground() {
  if (pageSuspended) return;
  pageSuspended = true;
  disconnect();
  setState('waiting', '', 'vueDesktop.status.connectingVideo');
  if (canvas.value) canvas.value.dataset.videoSuspended = 'true';
}

function clearPageSuspended() {
  pageSuspended = false;
  if (canvas.value) delete canvas.value.dataset.videoSuspended;
}

function resumeFromPageBackground() {
  if (!pageSuspended || document.hidden) return;
  clearPageSuspended();
  if (props.dataMode === 'demo') {
    nextTick(() => connect(props.tableIndex));
    return;
  }
  // App owns foreground session recovery. Waiting for its resume event keeps
  // /auth/video-watch behind the authenticated business socket reconnect.
  videoLogger.info('[wanli-video] foreground waiting for business socket recovery', { tableIndex: props.tableIndex });
}

function handleVisibilityChange() {
  if (document.hidden) suspendForPageBackground();
  else resumeFromPageBackground();
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', suspendForPageBackground);
  window.addEventListener('pageshow', resumeFromPageBackground);
  if (document.hidden) suspendForPageBackground();
  else nextTick(() => connect(props.tableIndex));
});
watch(() => props.tableIndex, (next) => connect(next));
watch(() => props.dataMode, () => connect(props.tableIndex));
watch(() => props.resumeEvent?.id, (id, previousId) => {
  if (!id || id === previousId || props.dataMode === 'demo' || document.hidden) return;
  // App's completed recovery is the authentication gate. Its Vue watcher can
  // run before this component's visibility callback at a microtask checkpoint.
  clearPageSuspended();
  videoLogger.info('[wanli-video] business socket recovery complete; reconnecting video', {
    tableIndex: props.tableIndex,
    resumeEventId: id,
    recoveryMode: props.resumeEvent?.mode || ''
  });
  nextTick(() => connect(props.tableIndex));
});
watch(() => props.t(activeMessageKey || 'vueDesktop.status.connectingVideo', statusFallback(activeMessageKey)), (message) => {
  if (activeState !== 'playing' && activeMessageKey) errorMessage.value = message;
});
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('pagehide', suspendForPageBackground);
  window.removeEventListener('pageshow', resumeFromPageBackground);
  disconnect();
});
</script>

<template>
  <div class="vue-game-video-surface" :class="{ 'is-waiting': waiting }">
    <canvas :key="canvasRevision" ref="canvas" class="vue-game-video-canvas" :aria-label="t('vueDesktop.label.realtimeVideo', 'Live table video')" />
    <div v-if="decoderBackend==='webcodecs' && !waiting" class="game-video-hd-badge" aria-label="HD">HD</div>
    <Transition name="wanli-video-ready">
      <div v-if="waiting" class="game-video-wait" role="status" aria-live="polite">
        <div class="game-wait-panel">
          <div class="game-wait-mark"><img src="/assets/brand/wanli-script-gold-v1.webp" alt="Wanli" /></div>
          <div class="game-wait-title">{{ errorMessage || t('vueDesktop.status.connectingVideo', 'Connecting to the VIP video') }}</div>
          <div class="game-wait-subtitle">{{ errorMessage ? 'VIDEO CONNECTION STATUS' : 'LIVE TABLE CONNECTION' }}</div>
          <div v-if="!errorMessage" class="game-wait-status" aria-hidden="true"><i /><i /><i /></div>
        </div>
      </div>
    </Transition>
  </div>
</template>
